import React, { useState, useEffect } from 'react';
import { apiService, Video } from '../services/api';
import { websocketService } from '../services/websocket';
import { getApiUrl } from '../lib/config';

interface PublicVideoPlayerProps {
  onLoginClick: () => void;
}

const PublicVideoPlayer: React.FC<PublicVideoPlayerProps> = ({ onLoginClick }) => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [genres, setGenres] = useState<string[]>([]);
  const [tags] = useState<string[]>([]);
  const [selectedGenre, setSelectedGenre] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [guestUsername, setGuestUsername] = useState('');
  const [isConnectedToChat, setIsConnectedToChat] = useState(false);

  useEffect(() => {
    loadVideos();
    loadGenres();
  }, []);

  useEffect(() => {
    loadVideos();
  }, [selectedGenre, searchTerm]);

  const loadVideos = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVideos({
        search: searchTerm,
        limit: 50
      });
      setVideos(response.videos);
      
      // Auto-select first video if none selected
      if (response.videos.length > 0 && !currentVideo) {
        setCurrentVideo(response.videos[0]);
      }
    } catch (err) {
      setError('Failed to load videos');
      console.error('Error loading videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadGenres = async () => {
    try {
              const response = await fetch(getApiUrl('api/videos/genres/list'));
      const data = await response.json();
      setGenres(data.genres || []);
    } catch (err) {
      console.error('Error loading genres:', err);
    }
  };

  const handleVideoSelect = (video: Video) => {
    setCurrentVideo(video);
    setShowChat(false); // Reset chat when changing videos
    setIsConnectedToChat(false);
  };

  const connectToChat = () => {
    if (!guestUsername.trim()) {
      alert('Please enter a username to join chat');
      return;
    }

    // Connect to WebSocket
    websocketService.connect();
    
    // Listen for chat messages
    websocketService.on('chat-message', (message: any) => {
      setChatMessages(prev => [...prev, message]);
    });

    setIsConnectedToChat(true);
    setShowChat(true);
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !isConnectedToChat) return;

    websocketService.sendChatMessage({
      streamId: 1, // For video chat, we can use a generic stream ID
      message: newMessage,
      username: guestUsername
    });

    setNewMessage('');
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="window-content p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">Loading videos...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-200">
      {/* Header */}
      <div className="window-header flex justify-between items-center">
        <div className="window-title">🎵 Radio Station - Free for Everyone</div>
        <div className="flex gap-2">
          <button
            onClick={onLoginClick}
            className="btn-3d px-3 py-1 text-sm"
          >
            Sign In (Optional)
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="window-content p-4 border-b">
        <div className="flex gap-4 items-center flex-wrap">
          <div className="flex-1 min-w-64">
            <input
              type="text"
              placeholder="Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-3d w-full"
            />
          </div>
          <div>
            <select
              value={selectedGenre}
              onChange={(e) => setSelectedGenre(e.target.value)}
              className="input-3d"
            >
              <option value="">All Genres</option>
              {genres.map(genre => (
                <option key={genre} value={genre}>{genre}</option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-600">
            {videos.length} videos available
          </div>
        </div>
      </div>

      <div className="flex-1 flex">
        {/* Video Player */}
        <div className="flex-1 flex flex-col">
          {currentVideo ? (
            <>
              {/* Video Display */}
              <div className="window-inset p-4 m-4 mb-2">
                <div className="aspect-video bg-black rounded mb-4 flex items-center justify-center">
                  {currentVideo.file_path ? (
                    <video
                      key={currentVideo.id}
                      controls
                      className="w-full h-full rounded"
                      poster={currentVideo.thumbnail_url}
                    >
                      <source src={currentVideo.file_path} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <div className="text-white text-center">
                      <div className="text-2xl mb-2">🎵</div>
                      <div>Audio Only</div>
                    </div>
                  )}
                </div>

                {/* Video Info */}
                <div className="space-y-2">
                  <h2 className="text-xl font-bold">{currentVideo.title}</h2>
                  {currentVideo.description && (
                    <p className="text-gray-700">{currentVideo.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span>👁️ {currentVideo.view_count} views</span>
                    {currentVideo.duration_seconds && (
                      <span>⏱️ {formatDuration(currentVideo.duration_seconds)}</span>
                    )}
                    {currentVideo.show_title && (
                      <span>📺 {currentVideo.show_title}</span>
                    )}
                  </div>
                  {currentVideo.uploader_name && (
                    <div className="text-sm text-gray-600">
                      🎙️ Uploaded by {currentVideo.uploader_name}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Section */}
              <div className="window-inset p-4 m-4 mt-2">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold">💬 Live Chat</h3>
                  {!showChat && (
                    <button
                      onClick={() => setShowChat(true)}
                      className="btn-3d px-3 py-1 text-sm"
                    >
                      Join Chat
                    </button>
                  )}
                </div>

                {showChat && !isConnectedToChat && (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600">
                      Chat is optional! Enter a username to participate in discussions.
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Choose a username..."
                        value={guestUsername}
                        onChange={(e) => setGuestUsername(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && connectToChat()}
                        className="input-3d flex-1"
                        maxLength={20}
                      />
                      <button
                        onClick={connectToChat}
                        className="btn-3d px-4"
                        disabled={!guestUsername.trim()}
                      >
                        Join
                      </button>
                    </div>
                  </div>
                )}

                {isConnectedToChat && (
                  <div className="space-y-3">
                    {/* Chat Messages */}
                    <div className="h-32 overflow-y-auto bg-white p-2 border border-gray-400 rounded">
                      {chatMessages.length === 0 ? (
                        <div className="text-gray-500 text-sm italic">
                          No messages yet. Start the conversation!
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {chatMessages.map((msg, index) => (
                            <div key={index} className="text-sm">
                              <span className="font-semibold text-blue-600">
                                {msg.username}:
                              </span>{' '}
                              <span className="text-gray-800">{msg.message}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                        className="input-3d flex-1"
                        maxLength={200}
                      />
                      <button
                        onClick={sendMessage}
                        className="btn-3d px-4"
                        disabled={!newMessage.trim()}
                      >
                        Send
                      </button>
                    </div>
                    <div className="text-xs text-gray-500">
                      Chatting as: <strong>{guestUsername}</strong> (guest)
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-600">
                <div className="text-4xl mb-4">🎵</div>
                <div>Select a video to start listening</div>
              </div>
            </div>
          )}
        </div>

        {/* Video List Sidebar */}
        <div className="w-80 border-l border-gray-400 bg-gray-100">
          <div className="window-header">
            <div className="window-title text-sm">📋 Available Videos</div>
          </div>
          <div className="window-content p-2 h-full overflow-y-auto">
            {error ? (
              <div className="text-red-600 text-sm p-4 text-center">{error}</div>
            ) : videos.length === 0 ? (
              <div className="text-gray-500 text-sm p-4 text-center">
                No videos found
              </div>
            ) : (
              <div className="space-y-2">
                {videos.map((video) => (
                  <div
                    key={video.id}
                    onClick={() => handleVideoSelect(video)}
                    className={`cursor-pointer p-3 rounded border transition-colors ${
                      currentVideo?.id === video.id
                        ? 'bg-blue-100 border-blue-400'
                        : 'bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-semibold text-sm mb-1 line-clamp-2">
                      {video.title}
                    </div>
                    <div className="text-xs text-gray-600 space-y-1">
                      {video.uploader_name && (
                        <div>🎙️ {video.uploader_name}</div>
                      )}
                      <div className="flex justify-between">
                        <span>👁️ {video.view_count}</span>
                        {video.duration_seconds && (
                          <span>⏱️ {formatDuration(video.duration_seconds)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicVideoPlayer; 